import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { ResourceRow } from './ResourceTabCard';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { NULL_VALUE } from 'constants/displayConstants';
import { selectIncident, useAppSelector } from 'slices/commonSelectors';
import { useAors } from 'hooks/useAors';
import IncidentDetailsSummary from './IncidentDetailsSummary';
import { USCG_SYMBOLOGY_OPTIONS } from '../LayersTab/utils/uscgSymbology';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import {
  symbolToPictureMarkerMap,
  type UscgSymbol,
} from '../LayersTab/utils/uscgSymbology';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import { Link } from 'react-router';
import { useGetLatestIncidentStatusForUser } from 'hooks/incident/useWorkLog';
import { useEffect } from 'react';

const getAssetStatusIndicator = (status: string | undefined) => {
  if (!status) return null;
  if (status === 'FMC') {
    return { iconName: 'arrow_drop_up', color: 'success' } as const;
  }
  if (status === 'PMC') {
    return { iconName: 'arrow_right', color: 'warning' } as const;
  }
  // NMC, NMCM, NMCS, NMCD
  if (status.startsWith('NMC')) {
    return { iconName: 'arrow_drop_down', color: 'danger' } as const;
  }
  return null;
};

type ViewResourceCardProps = {
  row: ResourceRow;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  setIsEditing: (isEditing: boolean) => void;
  setShowCheckInDrawer: (show: boolean) => void;
  setShowCheckOutDrawer?: (show: boolean) => void;
  setSelectedResource?: (resource: string | null) => void;
  viewOnly?: boolean;
  compact?: boolean;
};

const ViewResourceCard = ({
  row,
  expanded,
  setExpanded,
  setIsEditing,
  setShowCheckInDrawer,
  setShowCheckOutDrawer,
  setSelectedResource,
  viewOnly,
  compact,
}: ViewResourceCardProps) => {
  const incident = useAppSelector(selectIncident);
  const { data: aors = [] } = useAors();
  const { zoomToLocation, mapView } = useUscgMapContext();
  const { mutate: fetchStatuses, data: personnelStatusData } =
    useGetLatestIncidentStatusForUser();
  const currentIncidentId = incident?.id;
  const hasLocation = row.latitude != null && row.longitude != null;

  const incidentName =
    row?.incident_details?.[0]?.name || row?.incident?.name || NULL_VALUE;

  const assetStatusIndicator = getAssetStatusIndicator(row.assetStatus);
  const assetStatusTimestampFormatted = row.assetStatusTimestamp
    ? dayjs(row.assetStatusTimestamp).format('MM/DD/YYYY HH:mm')
    : null;

  const canCheckInOut =
    !!currentIncidentId &&
    (row.incident_details?.some(
      (incidentDetail) => incidentDetail.id === currentIncidentId
    ) ||
      row.incident?.id === currentIncidentId);

  useEffect(() => {
    if (
      row.assignees &&
      row.assignees?.length > 0 &&
      row.incident?.id &&
      row.kind === 'Personnel'
    ) {
      fetchStatuses({
        users: row.assignees || [],
        incidentId: row.incident?.id || '',
      });
    }
  }, [row.assignees, row.incident?.id, row.kind, fetchStatuses]);

  return (
    <>
      <div onClick={() => setExpanded(!expanded)} role="button">
        <div
          className={
            compact ? 'compact-resources-card-header' : 'resources-card-header'
          }
        >
          <div>
            {!expanded ? (
              <SharedIcon iconName="keyboard_arrow_right" size="sm" />
            ) : (
              <SharedIcon iconName="keyboard_arrow_down" size="sm" />
            )}
          </div>
          <div>{row.resource_set_id?.slice(0, 5)}</div>
          <div id="Resource Name">
            {row.name}
            {row.owning_organization ? ' (EXTERNAL)' : ''}
          </div>
          {!compact && (
            <>
              <div id="Kind">{row.kind}</div>
              <div id="AOR">
                {aors.find((a) => a.id === row.aor)?.name || NULL_VALUE}
              </div>
              <div id="Incident">{incidentName}</div>
              <div id="Work Availability">{row.status || NULL_VALUE}</div>
              <div id="Request Status">
                {row.inRequest ? 'Requested' : 'Not Requested'}
              </div>
            </>
          )}
          <div
            id="Asset Status"
            className="d-flex align-items-center gap-1 overflow-hidden text-nowrap"
          >
            <span>{row.assetStatus || NULL_VALUE}</span>
            {assetStatusIndicator && (
              <SharedIcon
                iconName={assetStatusIndicator.iconName}
                color={assetStatusIndicator.color}
              />
            )}
            {assetStatusTimestampFormatted && (
              <span className="text-truncate">
                {assetStatusTimestampFormatted}
              </span>
            )}
          </div>
          {!viewOnly && (
            <div className="d-flex flex-nowrap">
              <StylishNewButton
                className="button--icon me-1"
                onClick={(e) => {
                  e.stopPropagation();
                  if (compact && hasLocation) {
                    // Remove any previous resource highlight graphic
                    const existing = mapView?.graphics.filter(
                      (g) => g.attributes?.resourceHighlight
                    );
                    existing?.forEach((g) => mapView?.graphics.remove(g));

                    // Add symbology icon if resource has one
                    if (
                      row.symbology &&
                      mapView &&
                      symbolToPictureMarkerMap[row.symbology as UscgSymbol]
                    ) {
                      const graphic = new Graphic({
                        geometry: new Point({
                          longitude: row.longitude!,
                          latitude: row.latitude!,
                        }),
                        symbol:
                          symbolToPictureMarkerMap[row.symbology as UscgSymbol],
                        attributes: { resourceHighlight: true },
                      });
                      mapView.graphics.add(graphic);
                    }

                    zoomToLocation({
                      latitude: row.latitude!,
                      longitude: row.longitude!,
                    });
                  }
                }}
                disabled={!compact || !hasLocation}
              >
                <SharedIcon iconName="map" size="sm" />
              </StylishNewButton>
              <StylishNewButton
                className="button--icon"
                onClick={(e) => {
                  setIsEditing(true);
                  e.stopPropagation();
                }}
              >
                <SharedIcon iconName="edit" size="sm" />
              </StylishNewButton>
            </div>
          )}
        </div>
      </div>
      {expanded && (
        <div className="resource-card-content p-4">
          <div className="resource-card-section row gx-1 gy-2">
            <div className="col-xl-4 col-md-6">
              <div className="fw-semibold">Assignees</div>
              {Array.isArray(row?.assignees) && row.assignees.length > 0 ? (
                row.assignees.length === 1 && !row.assignees[0] ? (
                  <div>{NULL_VALUE}</div>
                ) : (
                  row.assignees.map((assignee) => (
                    <div key={assignee.userGuid}>
                      {assignee.userName || assignee.emailAddress}
                    </div>
                  ))
                )
              ) : (
                <div>{NULL_VALUE}</div>
              )}
            </div>
            <div className="col-3">
              <div>
                {row.kind === 'Personnel' && incident.id ? (
                  <Link to={`/iap/${incident.id}/roster`}>
                    Linked Incident Roster Position
                  </Link>
                ) : (
                  NULL_VALUE
                )}
              </div>
            </div>
            <div className="col-xl-3 col-md-6">
              <div className="fw-semibold">Current Location</div>
              <div>{row.address || NULL_VALUE}</div>
            </div>
            <div className="col-xl-4 col-md-6">
              <div className="fw-semibold">OPCON</div>
              <div>
                {aors.find((a) => a.id === row.opcon)?.name || NULL_VALUE}
              </div>
            </div>
            <div className="col-xl-3 col-md-6">
              <div className="fw-semibold">TACON</div>
              <div>
                {aors.find((a) => a.id === row.tacon)?.name || NULL_VALUE}
              </div>
            </div>
          </div>
          {row.kind === 'Personnel' && (
            <div className="resource-card-section row gx-1 gy-2">
              <div className="col-xl-4 col-lg-6">
                <div className="fw-semibold">Assignees</div>
                {Array.isArray(row?.assignees) &&
                row.assignees.length > 0 &&
                !(row.assignees.length === 1 && !row.assignees[0]) ? (
                  row.assignees.map((assignee) => (
                    <div key={assignee.userGuid}>
                      {assignee.userName || assignee.emailAddress}
                    </div>
                  ))
                ) : (
                  <div>{NULL_VALUE}</div>
                )}
              </div>
              <div className="col-xl-4 col-lg-6">
                <div className="fw-semibold">
                  Linked Incident Roster Position
                </div>
                <div>{row.name || NULL_VALUE}</div>
              </div>
            </div>
          )}
          <div className="resource-card-section row gx-1 gy-2">
            <div className="col-xl-3 col-md-6">
              <div className="fw-semibold">Point of Contact</div>
              <div>{row?.pointOfContact?.emailAddress || NULL_VALUE}</div>
            </div>
            <div className="col-xl-3 col-md-6">
              <div className="fw-semibold">Owning Organization</div>
              <div>{row.owning_organization || NULL_VALUE}</div>
            </div>
            <div className="col-xl-2 col-md-6">
              <div className="fw-semibold">Quantity</div>
              <div>{row.quantity ?? NULL_VALUE}</div>
            </div>
            <div className="col-xl-2 col-md-6">
              <div className="fw-semibold">Unit</div>
              <div>{row.unit || NULL_VALUE}</div>
            </div>
            <div className="col-xl-2 col-md-6">
              <div className="fw-semibold">Hull/Tail Number</div>
              <div>{row.hull_tail_number || NULL_VALUE}</div>
            </div>
          </div>
          <div className="resource-card-section row gx-1 gy-2 align-items-start">
            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="fw-semibold">Symbology</div>
              <div>
                {row.symbology
                  ? (() => {
                      const match = USCG_SYMBOLOGY_OPTIONS.find(
                        (opt) => opt.type === row.symbology
                      );
                      return match ? (
                        <span className="d-flex align-items-center gap-1">
                          <img
                            src={match.iconSrc}
                            alt=""
                            className="symbology-icon"
                          />
                          <span>{match.label}</span>
                        </span>
                      ) : (
                        row.symbology
                      );
                    })()
                  : NULL_VALUE}
              </div>
            </div>
            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="fw-semibold">Lat</div>
              <div>
                {row.latitude != null
                  ? Math.round(row.latitude * 100) / 100
                  : NULL_VALUE}
              </div>
            </div>
            <div className="col-xl-3 col-lg-4 col-md-6">
              <div className="fw-semibold">Long</div>
              <div>
                {row.longitude != null
                  ? Math.round(row.longitude * 100) / 100
                  : NULL_VALUE}
              </div>
            </div>
          </div>
          <div className="resource-card-section">
            <div className="fw-semibold">Capabilities</div>
            <div>{row.description || NULL_VALUE}</div>
          </div>
          <div className="resource-card-section row gx-1 gy-2">
            {row?.incident_details?.[0]?.id && (
              <div className="col-6 border rounded p-3">
                <IncidentDetailsSummary
                  incidentId={row.incident_details[0].id}
                  resourceSetId={row.resource_set_id}
                  datetimeOrdered={row.datetimeOrdered}
                  datetimeOrderedOffset={row.datetimeOrderedOffset}
                  eta={row.eta}
                  etaTimezoneOffset={row.etaTimezoneOffset}
                  notes={row.notes}
                />
              </div>
            )}
            <div className="col-6">
              {!viewOnly && canCheckInOut && (
                <>
                  <span className="fw-semibold">Check-in Status</span>
                  {row.kind === 'Personnel' ? (
                    personnelStatusData?.map((person) => (
                      <div
                        key={person.userName}
                        className="d-flex align-items-center mt-1"
                      >
                        <div className="col-3">{person.userName}: </div>
                        <div
                          className={classNames(
                            'col-2 me-2 resource-checkin-status-badge',
                            {
                              'resource-checkin-status-badge--pending-activation':
                                person.status === 'Pending Activation',
                              'resource-checkin-status-badge--declined':
                                person.status === 'Declined',
                              'resource-checkin-status-badge--activated':
                                person.status === 'Activated',
                              'resource-checkin-status-badge--checked-in':
                                person.status === 'Checked In',
                              'resource-checkin-status-badge--signed-in':
                                person.status === 'Signed In',
                            }
                          )}
                        >
                          {person.status ?? NULL_VALUE}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="d-flex align-items-center mt-1">
                      <div
                        className={classNames(
                          'me-2 resource-checkin-status-badge',
                          {
                            'resource-checkin-status-badge--not-arrived':
                              row.status === 'Not Arrived',
                            'resource-checkin-status-badge--on-site':
                              row.status === 'On Site',
                            'resource-checkin-status-badge--checked-in':
                              row.status === 'Checked In',
                          }
                        )}
                      >
                        {row.status ?? NULL_VALUE}
                      </div>
                      {row.status === 'On Site' && (
                        <StylishNewButton
                          onClick={() => {
                            setSelectedResource?.(row?.resource_set_id || '');
                            if (row?.resource_set_id) {
                              setShowCheckInDrawer(true);
                            }
                          }}
                        >
                          Check-In
                        </StylishNewButton>
                      )}
                      {row.status === 'Checked In' && (
                        <StylishNewButton
                          onClick={() => {
                            setSelectedResource?.(row?.resource_set_id || '');
                            if (row?.resource_set_id) {
                              setShowCheckOutDrawer?.(true);
                            }
                          }}
                        >
                          Demobilize
                        </StylishNewButton>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewResourceCard;
